import { useState, useRef, useEffect } from "react";
import { Play, Pause, Volume2 } from "lucide-react";

interface AudioFile {
  type?: string;
  url?: string;
  name?: string;
  size?: number;
  mimeType?: string;
  duration?: number;
}

interface AudioPlayerProps {
  files: (AudioFile | string)[];
  showDownload?: boolean;
  isOwnMessage?: boolean;
}

export function AudioPlayer({ files, isOwnMessage = true }: AudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  const getAudioUrl = () => {
    if (files.length === 0) return null;
    const firstFile = files[0];
    if (typeof firstFile === "string") return firstFile;
    if (firstFile && typeof firstFile === "object") return firstFile.url;
    return null;
  };

  const audioUrl = getAudioUrl();

  useEffect(() => {
    if (audioRef.current && audioUrl) {
      const audio = audioRef.current;
      let triedForceSeek = false;

      const handleLoadedMetadata = () => {
        setIsLoading(false);
        if (
          audio.duration &&
          !isNaN(audio.duration) &&
          isFinite(audio.duration)
        ) {
          setDuration(audio.duration);
        } else if (!triedForceSeek) {
          triedForceSeek = true;
          const originalTime = audio.currentTime;
          const seekToEnd = () => {
            if (
              audio.duration &&
              !isNaN(audio.duration) &&
              isFinite(audio.duration)
            ) {
              setDuration(audio.duration);
              audio.currentTime = originalTime;
              audio.removeEventListener("timeupdate", seekToEnd);
            }
          };
          audio.addEventListener("timeupdate", seekToEnd);
          try {
            audio.currentTime = 1e10;
          } catch {}
        }
      };

      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
        if (
          (!duration || isNaN(duration)) &&
          audio.duration &&
          !isNaN(audio.duration) &&
          isFinite(audio.duration)
        ) {
          setDuration(audio.duration);
        }
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };

      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("ended", handleEnded);

      return () => {
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
        audio.removeEventListener("timeupdate", handleTimeUpdate);
        audio.removeEventListener("ended", handleEnded);
      };
    }
  }, [audioUrl]);

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (audioRef.current && duration > 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      const newTime = percent * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (time: number) => {
    if (!time || isNaN(time) || !isFinite(time) || time <= 0) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  if (!audioUrl) {
    return null;
  }

  return (
    <div>
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      <div className="flex items-center gap-2">
        <div className="p-2 rounded-lg">
          <Volume2 size={18} className="text-azul-500" />
        </div>
        <p
          className={`text-xs ${
            isOwnMessage ? "text-gray-100" : "text-gray-600"
          }`}
        >
          Mensagem de áudio
        </p>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={togglePlayPause}
          disabled={isLoading}
          className="p-3 bg-azul-500 text-white rounded-full bg-azul-300"
          title={isPlaying ? "Pausar" : "Reproduzir"}
        >
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : isPlaying ? (
            <Pause size={18} />
          ) : (
            <Play size={18} className="ml-0.5" />
          )}
        </button>

        <div className="flex-1">
          <div
            className="bg-gray-200 rounded-full h-2 cursor-pointer hover:bg-gray-300 transition-colors"
            onClick={handleSeek}
          >
            <div
              className="bg-azul-500 h-2 rounded-full transition-all duration-100 shadow-sm"
              style={{
                width:
                  duration > 0 ? `${(currentTime / duration) * 100}%` : "0%",
              }}
            />
          </div>

          <div
            className={`flex justify-between text-xs mt-2 font-medium ${
              isOwnMessage ? "text-gray-100" : "text-gray-600"
            }`}
          >
            <span>{formatTime(currentTime)}</span>
            <span>{duration > 0 ? formatTime(duration) : "0:00"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
